import {useHistory, useLocation, useParams} from 'react-router-dom'
import {FieldErrors, useForm} from 'react-hook-form'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import { loadApplication, HouseholdWarningRequiredError, getCoApplicantInviteStatus, getCoApplicantPrimaryApplicant } from '../../api'
import {
  IApplicationFormData,
  IWizardPet,
  IWizardVehicle,
  createEmptyApplicationFormData,
  normalizeApplicationFormData,
} from '../../models/application-form'
import {IStepConfig, IWizardStepActionResult} from '../../models/step-config'
import {reconcileSavedOtherOccupants} from '../../models/other-occupants'
import {stepConfig} from './stepConfig'
import {resolveWizardRedirectPath, resolveWizardResumePath, toOptionalBoolean} from './wizardAccess'
import {
    buildCoApplicantInviteInitialData,
    getFullName,
    getPrimaryApplicantName,
    isCoApplicantApplication,
    IsInviteApplication
} from './coApplicantInviteContext'

const APPLICATION_WIZARD_PATH = 'application-wizard'
const APPLICATION_LANDING_PATH = 'application-landing'
const RENTER_HOME_PATH = 'my-doora-card'
const RENTER_PROFILE_EDIT_PATH = 'my-doora-card/edit'

type UseWizardOptions = {
  isRenterProfileEdit?: boolean
}

type WizardLocationState = {
  initialApplicationData?: IApplicationFormData
  wizardScrollTarget?: string
}

const normalizeValidationPath = (path: string): string => path.replace(/\[(\d+)\]/g, '.$1')

const serializeForComparison = (data: IApplicationFormData): string => {
  const snapshot = {
    ...data,
    otpCode: undefined,
    reviewSummary: undefined,
    lastSavedAtUtc: undefined,
  }

  return JSON.stringify(snapshot)
}

const toActionErrorMessage = (result?: IWizardStepActionResult): string => {
  if (!result?.error) {
    return 'Unable to process this step.'
  }

  if (typeof result.error === 'string') {
    return result.error
  }

  if (result.error instanceof Error) {
    return result.error.message
  }

  return 'Unable to process this step.'
}

const petsMatch = (left: IWizardPet, right: IWizardPet): boolean =>
  left.type === right.type &&
  left.breed === right.breed &&
  left.isAssistanceAnimal === right.isAssistanceAnimal

const vehiclesMatch = (left: IWizardVehicle, right: IWizardVehicle): boolean =>
  left.makeModel === right.makeModel && left.registrationNumber === right.registrationNumber

const mergePersistedItemIds = <T extends {id?: number}>(
  currentItems: T[],
  submittedItems: T[],
  persistedItems: T[] | undefined,
  matches: (left: T, right: T) => boolean
): T[] => {
  if (!persistedItems?.length) {
    return currentItems
  }

  const usedSubmittedIndexes = new Set<number>()
  const usedPersistedIndexes = new Set<number>()

  return currentItems.map((currentItem) => {
    if (currentItem.id !== undefined) {
      return currentItem
    }

    const submittedIndex = submittedItems.findIndex(
      (submittedItem, index) =>
        !usedSubmittedIndexes.has(index) &&
        submittedItem.id === undefined &&
        matches(currentItem, submittedItem)
    )

    if (submittedIndex < 0) {
      return currentItem
    }

    usedSubmittedIndexes.add(submittedIndex)
    const persistedIndex = persistedItems.findIndex(
      (persistedItem, index) =>
        !usedPersistedIndexes.has(index) &&
        persistedItem.id !== undefined &&
        matches(submittedItems[submittedIndex], persistedItem)
    )

    if (persistedIndex < 0) {
      return currentItem
    }

    usedPersistedIndexes.add(persistedIndex)
    return {
      ...currentItem,
      id: persistedItems[persistedIndex].id,
    }
  })
}

const getPersistedCardItemUpdates = (
  currentData: IApplicationFormData,
  submittedData: IApplicationFormData,
  result: IWizardStepActionResult
): Partial<IApplicationFormData> => {
  const updates: Partial<IApplicationFormData> = result.persistedOtherOccupants
    ? reconcileSavedOtherOccupants(currentData, submittedData, result.persistedOtherOccupants)
    : {}
  const petsWithIds = mergePersistedItemIds(
    currentData.pets,
    submittedData.pets,
    result.persistedPets,
    petsMatch
  )
  const vehiclesWithIds = mergePersistedItemIds(
    currentData.vehicles,
    submittedData.vehicles,
    result.persistedVehicles,
    vehiclesMatch
  )

  if (petsWithIds.some((pet, index) => pet.id !== currentData.pets[index]?.id)) {
    updates.pets = petsWithIds
  }
  if (vehiclesWithIds.some((vehicle, index) => vehicle.id !== currentData.vehicles[index]?.id)) {
    updates.vehicles = vehiclesWithIds
  }
  return updates
}

const serializeAcknowledgedSave = (
  submittedData: IApplicationFormData,
  result: IWizardStepActionResult
): string => {
  const acknowledgedData = normalizeApplicationFormData({...submittedData, ...result.updatedValues})
  return serializeForComparison(
    normalizeApplicationFormData({
      ...acknowledgedData,
      ...getPersistedCardItemUpdates(acknowledgedData, submittedData, result),
    })
  )
}

const hasValidationValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false
  }

  return typeof value !== 'string' || value.trim().length > 0
}

type ValidationLogItem = {
  field: string
  message: string
}

const getValidationLogItems = (error: unknown): ValidationLogItem[] => {
  const yupError = error as {
    inner?: Array<{path?: string; message?: string}>
    path?: string
    message?: string
  }
  const issues = yupError.inner?.length ? yupError.inner : [yupError]

  return issues
    .filter((item): item is {path?: string; message: string} => typeof item.message === 'string')
    .map((item) => ({
      field: item.path ? normalizeValidationPath(item.path) : '(form)',
      message: item.message,
    }))
}

const loadInvitedApplicationContext = async (
  sessionId: string,
  loadedDraft: IApplicationFormData
): Promise<Partial<IApplicationFormData>> => {
  if (!IsInviteApplication(loadedDraft)) return {}

  try {
    const primaryApplicant = await getCoApplicantPrimaryApplicant(sessionId)
    return {primaryApplicantName: getPrimaryApplicantName(primaryApplicant) || loadedDraft.primaryApplicantName}
  } catch {
    return {}
  }
}

export function useWizard({isRenterProfileEdit = false}: UseWizardOptions = {}) {
  const history = useHistory()
  const location = useLocation<WizardLocationState>()
  const {stepParam} = useParams<{stepParam: string}>()
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [isSubmittingStep, setIsSubmittingStep] = useState(false)
  const sessionIdFromQuery = useMemo(() => {
    const value = new URLSearchParams(location.search).get('sessionId')
    return value?.trim() || undefined
  }, [location.search])
  const activeSessionRef = useRef(sessionIdFromQuery)
  activeSessionRef.current = sessionIdFromQuery
  const [householdJoinSessionId, setHouseholdJoinSessionId] = useState<string | null>(null)
  const isHouseholdJoinPending = Boolean(sessionIdFromQuery && householdJoinSessionId === sessionIdFromQuery)

  const inviteIdFromQuery = useMemo(() => {
    const value = new URLSearchParams(location.search).get('inviteId')
    return value?.trim() || undefined
  }, [location.search])

  const initialApplicationData = useMemo(() => {
    const initialData = location.state?.initialApplicationData
    if (!initialData) {
      return undefined
    }

    const normalized = normalizeApplicationFormData({
      ...initialData,
      sessionId: initialData.sessionId ?? sessionIdFromQuery,
      coApplicantInviteId: initialData.coApplicantInviteId ?? inviteIdFromQuery,
    })

    if (sessionIdFromQuery && normalized.sessionId && normalized.sessionId !== sessionIdFromQuery) {
      return undefined
    }

    return normalized
  }, [inviteIdFromQuery, location.state, sessionIdFromQuery])
  const [isHydratingFromSession, setIsHydratingFromSession] = useState(() =>
    Boolean(sessionIdFromQuery && !initialApplicationData)
  )

  const [isHydratingFromInvite, setIsHydratingFromInvite] = useState(() =>
    Boolean(inviteIdFromQuery && !sessionIdFromQuery && !initialApplicationData)
  )

  const defaultValues: IApplicationFormData = useMemo(
    () => initialApplicationData ?? normalizeApplicationFormData(createEmptyApplicationFormData()),
    [initialApplicationData]
  )

  const getActiveSteps = (data: IApplicationFormData): IStepConfig[] =>
    stepConfig.filter((step) => (step.condition ? step.condition(data) : true))

  const form = useForm<IApplicationFormData>({
    defaultValues,
    mode: 'onChange',
    resolver: async (data) => {
      const normalized = normalizeApplicationFormData(data)
      const activeSteps = getActiveSteps(normalized)
      const current = activeSteps.find((step) => step.path === stepParam)

      if (!current?.schema) {
        return {values: normalized, errors: {}}
      }

      try {
        await current.schema.validate(normalized, {
          abortEarly: false,
          context: {BlockNotVerified: data.blockNotVerifiedCoApplicant},
        })
        return {values: normalized, errors: {}}
      } catch (error: unknown) {
        const yupError = error as {inner?: Array<{path?: string; message: string}>}
        const formErrors: FieldErrors<IApplicationFormData> = {}

        for (const item of yupError.inner ?? []) {
          if (!item.path) {
            continue
          }

          const normalizedPath = normalizeValidationPath(item.path)
          formErrors[normalizedPath as keyof IApplicationFormData] = {
            type: 'manual',
            message: item.message,
          }
        }

        return {values: {}, errors: formErrors}
      }
    },
  })

  const {watch, trigger, getValues, reset, setValue} = form
  const formData = normalizeApplicationFormData(watch())
  const hydratedApplicationRef = useRef<number | null>(
    initialApplicationData?.applicationId ?? null
  )
  const lastHandledActionRef = useRef<string | undefined>(undefined)
  const pendingStepParamRef = useRef<string | null>(null)

  const applyUpdatedValues = (
    updatedValues?: Partial<IApplicationFormData>,
    options?: {preserveErrors?: boolean; submittedData?: IApplicationFormData}
  ) => {
    if (!updatedValues || Object.keys(updatedValues).length === 0) {
      return
    }

    const acknowledgedValues = {...updatedValues}
    if (
      options?.submittedData &&
      getValues('retentionPreferenceSelection') !== options.submittedData.retentionPreferenceSelection
    ) {
      // A completed save must not consume a radio choice made while it was pending.
      delete acknowledgedValues.retentionPreferenceSelection
    }
    const merged = normalizeApplicationFormData({
      ...getValues(),
      ...acknowledgedValues,
    })

    if (options?.preserveErrors) {
      reset(merged, {
        keepErrors: true,
        keepTouched: true,
        keepDirty: true,
        keepIsValid: true,
        keepSubmitCount: true,
      })
      return
    }

    reset(merged)
  }

  const applyUpdatedValuesWithoutReset = (updatedValues?: Partial<IApplicationFormData>) => {
    if (!updatedValues || Object.keys(updatedValues).length === 0) {
      return
    }

    const merged = normalizeApplicationFormData({
      ...getValues(),
      ...updatedValues,
    })

    for (const key of Object.keys(updatedValues) as (keyof IApplicationFormData)[]) {
      setValue(key, merged[key] as never, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    }
  }

  const applyPersistedCardItemIds = (
    submittedData: IApplicationFormData,
    result: IWizardStepActionResult
  ) => {
    applyUpdatedValuesWithoutReset(
      getPersistedCardItemUpdates(normalizeApplicationFormData(getValues()), submittedData, result)
    )
  }

  const showHouseholdJoinWarning = useCallback((sessionId: string) => {
    if (activeSessionRef.current !== sessionId) return
    // A pending join has no onboarding draft yet. Render Profile without granting draft access.
    reset(normalizeApplicationFormData({sessionId, renterRole: 'co-applicant'}))
    hydratedApplicationRef.current = null
    setHouseholdJoinSessionId(sessionId)
    if (history.location.pathname !== '/application-wizard/profile') {
      history.replace(`/application-wizard/profile?sessionId=${encodeURIComponent(sessionId)}`)
    }
  }, [history, reset])

  const loadSessionValues = useCallback(async () => {
    if (!sessionIdFromQuery) throw new Error('Open your household invitation to continue.')
    const loaded = await loadApplication(sessionIdFromQuery)
    const householdContext = await loadInvitedApplicationContext(sessionIdFromQuery, loaded)
    return normalizeApplicationFormData({
      ...getValues(),
      ...loaded,
      ...householdContext,
      sessionId: loaded.sessionId ?? sessionIdFromQuery,
      isMobileVerified: loaded.isMobileVerified ?? true,
    })
  }, [getValues, sessionIdFromQuery])

  useEffect(() => {
    if (!sessionIdFromQuery) {
      setIsHydratingFromSession(false)
      return
    }

    if (initialApplicationData) {
      setIsHydratingFromSession(false)
      return
    }

    const current = normalizeApplicationFormData(getValues())
    if (
      current.sessionId === sessionIdFromQuery &&
      current.applicationId &&
      current.applicationId > 0 &&
      current.isMobileVerified
    ) {
      setIsHydratingFromSession(false)
      return
    }

    let cancelled = false
    setIsHydratingFromSession(true)

    const hydrateFromSession = async () => {
      const merged = await loadSessionValues()
      if (cancelled) {
        return
      }

      reset(merged)
      setHouseholdJoinSessionId(null)
      setIsHydratingFromSession(false)
    }

    hydrateFromSession().catch((error) => {
      if (cancelled) return
      if (error instanceof HouseholdWarningRequiredError) {
        showHouseholdJoinWarning(sessionIdFromQuery)
      } else {
        toast.error(error instanceof Error ? error.message : 'Unable to load your application.', {icon: false})
      }
      setIsHydratingFromSession(false)
    })

    return () => {
      cancelled = true
    }
  }, [getValues, initialApplicationData, loadSessionValues, reset, sessionIdFromQuery, showHouseholdJoinWarning])

  useEffect(() => {
    const activeInviteId = inviteIdFromQuery ?? ''

      if (!activeInviteId || sessionIdFromQuery || initialApplicationData) {
      setIsHydratingFromInvite(false)
      return
    }

    const current = normalizeApplicationFormData(getValues())
    if (current.isMobileVerified && current.sessionId && current.applicationId) {
      setIsHydratingFromInvite(false)
      return
    }

    const currentInviteId = current.coApplicantInviteId?.trim()
    const hasConfirmedInviteDetails =
      currentInviteId === inviteIdFromQuery &&
      isCoApplicantApplication(current) &&
      !!current.fullName?.trim() &&
      !!current.mobileNumber?.trim() &&
      current.termsAccepted === true &&
      current.consentSharingAccepted === true

    if (hasConfirmedInviteDetails) {
      setIsHydratingFromInvite(false)
      return
    }

    let cancelled = false
    setIsHydratingFromInvite(true)

    const hydrateFromInvite = async () => {
        try {
            const result = await getCoApplicantInviteStatus(activeInviteId)
        if (cancelled) {
          return
        }

        if (!result.found || !result.primaryApplicant || result.isExpired || result.isVerified || result.isDeclined) {
          history.replace(`/application-wizard/coapp-invite/${encodeURIComponent(activeInviteId)}`)
          return
        }

        const inviteData = buildCoApplicantInviteInitialData(result.primaryApplicant, activeInviteId)

        reset(
          normalizeApplicationFormData({
            ...getValues(),
            ...inviteData,
            fullName: getFullName(inviteData.firstName, inviteData.lastName),
          })
        )
      } catch {
        if (!cancelled) {
          toast.error('Unable to load invite details. Please open the invitation link again.', {
            icon: false,
          })
          history.replace(`/application-wizard/coapp-invite/${encodeURIComponent(activeInviteId)}`)
        }
      } finally {
        if (!cancelled) {
          setIsHydratingFromInvite(false)
        }
      }
    }

    hydrateFromInvite()

    return () => {
      cancelled = true
      }
  }, [getValues, history, initialApplicationData, inviteIdFromQuery, reset, sessionIdFromQuery])

  useEffect(() => {
    const applicationId = formData.applicationId
    if (!applicationId || hydratedApplicationRef.current === applicationId) {
      return
    }

    hydratedApplicationRef.current = applicationId
    let cancelled = false

    const hydrateFromApi = async () => {
      const loaded = await loadApplication(formData.sessionId)
      const householdContext = formData.sessionId ? await loadInvitedApplicationContext(formData.sessionId, loaded) : null;
      if (cancelled || !loaded.applicationId) {
        return
      }

      const currentValues = getValues()
      const shouldKeepApplicationTypeUnselected =
        currentValues.action !== 'resume' && !currentValues.applicationType

      reset(
        normalizeApplicationFormData({
          ...currentValues,
          ...loaded,
          ...householdContext,
          applicationType: shouldKeepApplicationTypeUnselected ? undefined : loaded.applicationType,
          applicationId,
        })
      )
    }

    hydrateFromApi().catch((error) => {
      if (cancelled) return
      if (error instanceof HouseholdWarningRequiredError && formData.sessionId) showHouseholdJoinWarning(formData.sessionId)
      else toast.error(error instanceof Error ? error.message : 'Unable to load your application.', {icon: false})
    })

    return () => {
      cancelled = true
    }
  }, [formData.applicationId, formData.sessionId, getValues, reset, sessionIdFromQuery, showHouseholdJoinWarning])

  useEffect(() => {
    if (formData.action === lastHandledActionRef.current) {
      return
    }

    lastHandledActionRef.current = formData.action
    if (formData.action !== 'new') {
      return
    }

    hydratedApplicationRef.current = null
    reset(
      normalizeApplicationFormData({
        ...getValues(),
        applicationId: undefined,
        applicationType: undefined,
        companyTrustAbnAcn: undefined,
        companyTrustDirectorId: undefined,
        companyTrustLetterOfAuthorityDocumentId: undefined,
        coApplicants: [],
        otpCode: '',
        otpCodeValid: false,
        otpRetryAfterSeconds: undefined,
        otpLockedUntilUtc: undefined,
        otpRequiresNewCode: false,
        isMobileVerified: false,
        mobileVerifiedAtUtc: undefined,
        pmEmail: undefined,
        submissionLinks: [],
        latestSubmissionUrl: undefined,
        latestSubmissionExpiresAtUtc: undefined,
        reviewSummary: null,
      })
    )
  }, [formData.action, getValues, reset])

  const activeSteps = useMemo(() => getActiveSteps(formData), [formData])
  const currentStep = activeSteps.find((step) => step.path === stepParam)
  const validateCurrentStepData = useCallback(
    (data: IApplicationFormData): boolean => {
      if (currentStep?.key === 'review') {
        const readiness = data.reviewSummary?.cardReadiness
        return (readiness?.canFinalize ?? readiness?.isComplete) === true
      }
      if (!currentStep?.schema) {
        return true
      }

      if (
        currentStep.key === 'rental-history' &&
        toOptionalBoolean(data.previousRentalHistory) === false
      ) {
        return true
      }

      try {
        currentStep.schema.validateSync(data, {
          abortEarly: false,
          context: {BlockNotVerified: data.blockNotVerifiedCoApplicant},
        })
        return true
      } catch {
        return false
      }
    },
    [currentStep]
  )
  const isCurrentStepValid = useMemo(
    () => validateCurrentStepData(formData),
    [formData, validateCurrentStepData]
  )

  useEffect(() => {
    if (isHouseholdJoinPending && sessionIdFromQuery) {
      if (stepParam !== 'profile') {
        history.replace(`/application-wizard/profile?sessionId=${encodeURIComponent(sessionIdFromQuery)}`)
      }
      return
    }
    if (isHydratingFromSession || isHydratingFromInvite || isSubmittingStep) {
      return
    }

    const pendingStepParam = pendingStepParamRef.current
    if (pendingStepParam) {
      if (pendingStepParam !== stepParam) {
        return
      }

      pendingStepParamRef.current = null
    }

    const redirectPath = resolveWizardRedirectPath({
      stepParam,
      currentStepPath: currentStep?.path,
      activeSteps,
      data: formData,
    })

    if (!redirectPath || redirectPath === stepParam) {
      return
    }

    const basePath = isRenterProfileEdit ? RENTER_PROFILE_EDIT_PATH : APPLICATION_WIZARD_PATH
    history.replace(`/${basePath}/${redirectPath}${location.search}`)
  }, [
    activeSteps,
    currentStep?.path,
    formData,
    history,
    isHydratingFromSession,
    isHydratingFromInvite,
    isHouseholdJoinPending,
    isRenterProfileEdit,
    isSubmittingStep,
    location.search,
    stepParam,
    sessionIdFromQuery,
  ])

  const lastSavedRef = useRef(serializeForComparison(formData))
  const reloadHouseholdJoinProfile = useCallback(async () => {
    const loaded = await loadSessionValues()
    if (activeSessionRef.current !== sessionIdFromQuery) return
    if (!loaded.applicationId) throw new Error('Unable to load your profile. Please try again.')
    hydratedApplicationRef.current = loaded.applicationId
    reset(loaded)
    lastSavedRef.current = serializeForComparison(loaded)
    setHouseholdJoinSessionId(null)
  }, [loadSessionValues, reset, sessionIdFromQuery])

  const logCurrentStepValidationErrors = async (data: IApplicationFormData) => {
    if (process.env.NODE_ENV === 'production' || !currentStep?.schema) {
      return
    }

    try {
      await currentStep.schema.validate(data, {
        abortEarly: false,
        context: {BlockNotVerified: data.blockNotVerifiedCoApplicant},
      })
    } catch (error: unknown) {
      const validationErrors = getValidationLogItems(error)
      if (validationErrors.length === 0) {
        return
      }

      console.groupCollapsed(`[wizard-validation] ${currentStep.key}`)
      console.table(validationErrors)
      console.groupEnd()
    }
  }

  const triggerValidation = async (): Promise<boolean> => {
    const values = getValues()
    const fieldsToValidate: (keyof IApplicationFormData)[] = []

    for (const fieldName in values) {
      if (hasValidationValue(values[fieldName as keyof IApplicationFormData])) {
        fieldsToValidate.push(fieldName as keyof IApplicationFormData)
      }
    }

    // Trigger validation only for fields with values
    const isValid = await trigger(fieldsToValidate)
    if (!isValid) {
      //await logCurrentStepValidationErrors(normalizeApplicationFormData(getValues()))
    }
    return isValid
  }

  const returnToRenterProfile = (sessionId?: string) => {
    const params = new URLSearchParams({tab: 'profile'})
    if (sessionId) {
      params.set('sessionId', sessionId)
    }
    history.replace(`/${RENTER_HOME_PATH}?${params.toString()}`)
  }

  const getNextLocationSearch = (data: IApplicationFormData): string => {
    const sessionId = data.sessionId?.trim()
    if (sessionId) {
      return `?sessionId=${encodeURIComponent(sessionId)}`
    }

    return location.search
  }

  const pushWizardStep = (stepPath: string, search: string) => {
    pendingStepParamRef.current = stepPath
    history.push(`/${APPLICATION_WIZARD_PATH}/${stepPath}${search}`)
  }

  useEffect(() => {
    if (
      isHouseholdJoinPending ||
      isHydratingFromSession ||
      isHydratingFromInvite ||
      !currentStep?.onAutoSave ||
      !formData.isMobileVerified ||
      !formData.applicationId ||
      !formData.sessionId?.trim()
    ) {
      return
    }

    const timer = window.setTimeout(async () => {
      // Form arrays are mutable, so retain exactly the input passed to this request.
      const submittedData = structuredClone(formData)
      const comparableSnapshot = serializeForComparison(submittedData)
      if (comparableSnapshot === lastSavedRef.current) {
        return
      }

      const result = await currentStep.onAutoSave!(submittedData)
      if (!result.success) {
        toast.error(toActionErrorMessage(result), {icon: false})
        return
      }

      applyUpdatedValues(result.updatedValues, {preserveErrors: true, submittedData})
      applyPersistedCardItemIds(submittedData, result)
      setLastSaved(new Date())
      // Acknowledge this request, not edits typed while it was pending.
      lastSavedRef.current = serializeAcknowledgedSave(submittedData, result)
      //toast.success('Draft autosaved.', {icon: false})
    }, 2000)

    return () => window.clearTimeout(timer)
  }, [currentStep, formData, getValues, isHouseholdJoinPending, isHydratingFromSession, isHydratingFromInvite])

  const next = async () => {
    if (!currentStep || isSubmittingStep || isHouseholdJoinPending || isHydratingFromSession || isHydratingFromInvite) {
      return
    }

    setIsSubmittingStep(true)
      try {
          const currentData = normalizeApplicationFormData(structuredClone(getValues()))

          if (!validateCurrentStepData(currentData)) {
              await trigger()
              return
          }

          const currentActiveSteps = getActiveSteps(currentData)
          const index = currentActiveSteps.findIndex((step) => step.path === stepParam)
          const nextStep = index >= 0 ? currentActiveSteps[index + 1] : undefined
          const shouldResumeToSavedStep =
              !isRenterProfileEdit &&
              currentStep.key === 'verify-mobile' &&
              currentData.action === 'resume'

          if (currentStep.onNext) {
              const result = await currentStep.onNext(currentData)
              const updatedData = normalizeApplicationFormData({
                  ...currentData,
                  ...result.updatedValues,
              })
              applyUpdatedValues(result.updatedValues, {submittedData: currentData})

              if (!result.success) {
                  toast.error(toActionErrorMessage(result), { icon: false })
                  return
              }

              if (result.redirectTo) {
                  history.replace(result.redirectTo)
                  return
              }

              applyPersistedCardItemIds(currentData, result)

              lastSavedRef.current = serializeAcknowledgedSave(currentData, result)
              if (currentStep.isSaveEnabled) {
                  setLastSaved(new Date())
                  //toast.success('Draft saved.', {icon: false})
              }

              if (shouldResumeToSavedStep) {
                  const resumePath = resolveWizardResumePath(getActiveSteps(updatedData), updatedData)
                  if (resumePath) {
                      pushWizardStep(resumePath, getNextLocationSearch(updatedData))
                      return
                  }
              }
          }

          if (isRenterProfileEdit) {
              const savedData = normalizeApplicationFormData(getValues())
              returnToRenterProfile(savedData.sessionId || currentData.sessionId)
              return
          }

          if (nextStep) {
              const savedData = normalizeApplicationFormData(getValues())
              pushWizardStep(nextStep.path, getNextLocationSearch(savedData))
              return
          }

          if (currentStep.key === 'review') {
              const finalData = normalizeApplicationFormData(getValues())
              const finalSessionId = finalData.sessionId || currentData.sessionId
              const query = finalSessionId ? `?sessionId=${encodeURIComponent(finalSessionId)}` : ''
              history.push(`/${RENTER_HOME_PATH}${query}`)
          }
      }
      finally {
          setIsSubmittingStep(false)
      }
  }

  const prev = async () => {
    if (!currentStep || isHouseholdJoinPending || isHydratingFromSession || isHydratingFromInvite) {
      return
    }

    const index = activeSteps.findIndex((step) => step.path === stepParam)
    const previousStep = activeSteps[index - 1]
    const resumeMarkerStep = previousStep ? activeSteps[index - 2] : undefined
    const currentData = normalizeApplicationFormData(structuredClone(getValues()))

    if (currentStep.onPrev) {
      const result = await currentStep.onPrev(currentData, {
        resumeStepKey: resumeMarkerStep?.key ?? previousStep?.key,
      })
      // The step data may have saved even when the following resume-marker save failed.
      applyPersistedCardItemIds(currentData, result)
      if (!result.success) {
        toast.error(toActionErrorMessage(result), {icon: false})
        return
      }

      applyUpdatedValues(result.updatedValues, {submittedData: currentData})
      lastSavedRef.current = serializeAcknowledgedSave(currentData, result)
      if (currentStep.isSaveEnabled) {
        setLastSaved(new Date())
      }
    }

    if (isRenterProfileEdit) {
      const savedData = normalizeApplicationFormData(getValues())
      returnToRenterProfile(savedData.sessionId || currentData.sessionId)
      return
    }

    if (previousStep) {
      pushWizardStep(previousStep.path, location.search)
      return
    }

    history.push(`/${APPLICATION_LANDING_PATH}`)
  }

  const progressSteps = activeSteps.filter((step) => step.showProgressBar)
  const stepIndex = progressSteps.findIndex((step) => step.path === stepParam) + 1
  const totalSteps = progressSteps.length

  return {
    form,
    next,
    prev,
    stepIndex: stepIndex > 0 ? stepIndex : 1,
    totalSteps,
    currentStep,
    isCurrentStepValid,
    isSubmittingStep,
    isHydratingFromSession: isHydratingFromSession || isHydratingFromInvite,
    isHouseholdJoinPending,
    reloadHouseholdJoinProfile,
    lastSaved,
    triggerValidation,
    wizardScrollTarget: location.state?.wizardScrollTarget,
  }
}
